import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

type Props = {
  children: React.ReactNode;
  offset: number;
  initialWidth: number;
  maxWidth?: number;
  minWidth?: number;
  disabled?: boolean;
  hideContent?: boolean;
  onChange?: (width: number) => void;
  onResizeStart?: (width: number) => void;
  onResizeStop?: (width: number) => void;
};

const DraggableHandle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  width: 4px;
  padding: 4px 0 0;
  top: 0;
  bottom: 0;
  right: 0;
  cursor: col-resize;
  z-index: var(--zindex-layer-09);
  height: 100%;
  transition: all var(--animation-duration-medium);

  &:hover {
    background-color: var(--blue-50);
    user-select: none;
  }
`;

const Wrapper = styled.div<{
  itemWidth: number;
  hideContent: boolean;
  isResizing: boolean;
}>`
  position: relative;
  width: ${(props) => (props.hideContent ? '0px' : `${props.itemWidth}px`)};
  height: 100%;
  user-select: none;
  /* Block transitons on width when resizing */
  transition: ${(props) => props.isResizing && 'width 0s !important'};
`;

export const Resizable = (props: Props) => {
  const {
    children,
    offset,
    maxWidth,
    minWidth,
    disabled = false,
    initialWidth,
    hideContent = false,
    onResizeStart,
    onResizeStop,
    onChange,
  } = props;
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(initialWidth);
  const onMouseDown = () => {
    setIsResizing(true);
    onResizeStart?.(width);
  };

  const onDoubleClick = () => {
    if (maxWidth && width < maxWidth) {
      setWidth(maxWidth);
      onChange?.(maxWidth);

      return;
    }
    if (minWidth && width === maxWidth) {
      setWidth(minWidth);
      onChange?.(minWidth);
    }
  };

  const onMouseUp = () => {
    setIsResizing(false);
    onResizeStop?.(width);
  };

  const onMouseMove = (e) => {
    if (isResizing) {
      const value = Math.min(
        Math.max(e.clientX - offset + 2, minWidth ?? 0),
        maxWidth ?? Infinity,
      );

      setWidth(() => {
        return value;
      });
      onChange?.(value);
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  });

  return (
    <Wrapper
      itemWidth={width ?? 256}
      hideContent={hideContent}
      data-resizing={isResizing}
      isResizing={isResizing}
      className="resizable-wrapper"
    >
      {children}
      {!disabled ? (
        <DraggableHandle
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
        />
      ) : null}
    </Wrapper>
  );
};
